import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import SettingsModal from '../components/SettingsModal';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, Loader2, Send, Banknote, ArrowRightLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ORDERED_VIA_OPTIONS = [
  'Viber', 'WhatsApp', 'Instagram', 'Facebook', 'Messenger',
  'Message', 'Call', 'TikTok', 'Website', 'Telegram', 'Other'
];

const NewOrder = () => {
  const { outletId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  
  const [outlet, setOutlet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [showCustomVia, setShowCustomVia] = useState(false);

  // Payment flow dialogs
  const [paymentReceivedDialog, setPaymentReceivedDialog] = useState(true);
  const [paymentMethodDialog, setPaymentMethodDialog] = useState(false);
  const [showForm, setShowForm] = useState(false);
  
  const [formData, setFormData] = useState({
    outlet_id: outletId,
    location: '',
    contact_no: '',
    item_details: '',
    quantity: '',
    payment_method: '',
    payment_status: '',
    ordered_via: '',
    customer_notes: ''
  });

  useEffect(() => {
    const fetchOutlet = async () => {
      try {
        const response = await axios.get(`${API}/outlets/${outletId}`, { headers: getAuthHeader() });
        setOutlet(response.data);
      } catch (e) {
        toast.error('Failed to load outlet');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchOutlet();
  }, [outletId, getAuthHeader, navigate]);

  // Step 1: Payment Received? → Yes or No
  const handlePaymentReceived = (received) => {
    setPaymentReceivedDialog(false);
    if (received) {
      setFormData(prev => ({ ...prev, payment_status: 'paid' }));
      setPaymentMethodDialog(true);
    } else {
      setPaymentMethodDialog(true);
    }
  };

  // Step 2: Select payment method (Cash / Transfer)
  const handlePaymentMethodSelect = (method) => {
    setPaymentMethodDialog(false);
    const status = formData.payment_status === 'paid' 
      ? 'paid' 
      : (method === 'cash' ? 'collect' : 'upon_delivery');
    setFormData(prev => ({ ...prev, payment_method: method, payment_status: status }));
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.location || !formData.contact_no) {
      toast.error('Please fill in required fields');
      return;
    }
    
    const submitData = {
      ...formData,
      quantity: formData.quantity ? parseInt(formData.quantity) : 1
    };
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/orders`, submitData, { headers: getAuthHeader() });
      toast.success('Order created!', { duration: 500 });
      setTimeout(() => navigate(`/orders/${outletId}`), 500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create order');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const paymentLabel = formData.payment_status === 'paid' 
    ? `${formData.payment_method === 'cash' ? 'Cash' : 'Transfer'} - Paid`
    : formData.payment_status === 'collect' ? 'Cash - Collect'
    : formData.payment_status === 'upon_delivery' ? 'Transfer - Upon Delivery'
    : '';

  return (
    <div className="min-h-screen bg-background" data-testid="new-order-page">
      <Navbar onOpenSettings={() => setSettingsOpen(true)} />
      
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/orders/${outletId}`)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">New Order</h1>
            <p className="text-sm text-muted-foreground">{outlet?.name}</p>
          </div>
        </div>

        {showForm && (
          <Card data-testid="new-order-form">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Order Details
                {paymentLabel && (
                  <span className={`text-sm font-medium ${formData.payment_status === 'paid' ? 'text-emerald-500' : 'text-red-500 font-bold'}`}>
                    {paymentLabel}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="location">Delivery Location *</Label>
                    <Input id="location" value={formData.location} onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))} placeholder="Enter delivery address" data-testid="new-order-location" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_no">Contact Number *</Label>
                    <Input id="contact_no" value={formData.contact_no} onChange={(e) => setFormData(prev => ({ ...prev, contact_no: e.target.value }))} placeholder="Customer phone number" data-testid="new-order-contact" />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="item_details">Item Details</Label>
                    <Input id="item_details" value={formData.item_details} onChange={(e) => setFormData(prev => ({ ...prev, item_details: e.target.value }))} placeholder="What is being delivered?" data-testid="new-order-items" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="quantity">Quantity</Label>
                    <Input id="quantity" type="number" min="1" value={formData.quantity} onChange={(e) => setFormData(prev => ({ ...prev, quantity: e.target.value }))} placeholder="Qty" data-testid="new-order-quantity" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Ordered Via</Label>
                  <Select
                    value={ORDERED_VIA_OPTIONS.includes(formData.ordered_via) ? formData.ordered_via : (formData.ordered_via ? 'Other' : '')}
                    onValueChange={(value) => {
                      if (value === 'Other') {
                        setShowCustomVia(true);
                        setFormData(prev => ({ ...prev, ordered_via: '' }));
                      } else {
                        setShowCustomVia(false);
                        setFormData(prev => ({ ...prev, ordered_via: value }));
                      }
                    }}
                  >
                    <SelectTrigger data-testid="new-order-via"><SelectValue placeholder="Select platform" /></SelectTrigger>
                    <SelectContent>
                      {ORDERED_VIA_OPTIONS.map((option) => (
                        <SelectItem key={option} value={option}>{option}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {showCustomVia && (
                    <Input value={formData.ordered_via} onChange={(e) => setFormData(prev => ({ ...prev, ordered_via: e.target.value }))} placeholder="Enter custom platform..." className="mt-2" autoFocus data-testid="new-order-via-custom" />
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="customer_notes">Customer Notes</Label>
                  <Textarea id="customer_notes" value={formData.customer_notes} onChange={(e) => setFormData(prev => ({ ...prev, customer_notes: e.target.value }))} placeholder="Any special instructions..." rows={3} data-testid="new-order-notes" />
                </div>

                <div className="flex justify-end pt-4">
                  <Button type="submit" disabled={submitting} data-testid="submit-order-btn">
                    {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    <Send className="h-4 w-4 mr-2" /> Create Order
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Step 1: Payment Received? */}
      <Dialog open={paymentReceivedDialog} onOpenChange={(open) => { if (!open) navigate(`/orders/${outletId}`); }}>
        <DialogContent data-testid="payment-received-dialog">
          <DialogHeader>
            <DialogTitle>Payment Received?</DialogTitle>
            <DialogDescription>Has the customer already paid for this order?</DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-3 sm:gap-0">
            <Button variant="outline" onClick={() => handlePaymentReceived(false)} data-testid="payment-received-no-btn">No</Button>
            <Button onClick={() => handlePaymentReceived(true)} data-testid="payment-received-yes-btn">Yes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Step 2: Select Payment Method */}
      <Dialog open={paymentMethodDialog} onOpenChange={(open) => { if (!open) navigate(`/orders/${outletId}`); }}>
        <DialogContent data-testid="payment-method-dialog">
          <DialogHeader>
            <DialogTitle>Payment Method</DialogTitle>
            <DialogDescription>
              {formData.payment_status === 'paid' ? 'How was the payment made?' : 'Select the payment method for this order.'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-4">
            <Button variant="outline" className="w-full justify-start text-left h-14" onClick={() => handlePaymentMethodSelect('cash')} data-testid="select-cash-btn">
              <Banknote className="h-5 w-5 mr-3 text-emerald-500" />
              <div>
                <p className="font-medium">Cash</p>
                <p className="text-xs text-muted-foreground">{formData.payment_status === 'paid' ? 'Paid in cash' : 'Collect on delivery'}</p>
              </div>
            </Button>
            <Button variant="outline" className="w-full justify-start text-left h-14" onClick={() => handlePaymentMethodSelect('transfer')} data-testid="select-transfer-btn">
              <ArrowRightLeft className="h-5 w-5 mr-3 text-blue-500" />
              <div>
                <p className="font-medium">Transfer</p>
                <p className="text-xs text-muted-foreground">{formData.payment_status === 'paid' ? 'Paid via transfer' : 'Upon delivery'}</p>
              </div>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </div>
  );
};

export default NewOrder;
